import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import AddIcon from '@mui/icons-material/Add';
import WifiIcon from '@mui/icons-material/Wifi';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import './tickets.css';
import TicketCard from './components/ticketCard';
import { getTickets, deleteTicket, getStatistics } from './apiTickets';
import useTicketWebSocket from './useTicketWebSocket';
import BASE_URL from '../../config';

const TicketList = () => {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [filters, setFilters] = useState({
    status: 'all',
    priority: 'all',
    issue_type: 'all',
    assigned_to_me: false,
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [statistics, setStatistics] = useState(null);
  const [showStats, setShowStats] = useState(false);

  // WebSocket connection for real-time updates
  const { isConnected, connectionStatus } = useTicketWebSocket({
    onTicketCreated: (ticket) => {
      console.log('New ticket created:', ticket);
      toast.info(`New ticket created: ${ticket.title}`);
      fetchTickets(); // Refresh ticket list
      if (isAdmin) loadStatistics(); // Refresh stats
    },
    onTicketUpdated: (ticket) => {
      console.log('Ticket updated:', ticket);
      // Update ticket in list without full refresh
      setTickets(prevTickets =>
        prevTickets.map(t => t.id === ticket.id ? { ...t, ...ticket } : t)
      );
      toast.info(`Ticket No: ${ticket.id} was updated`);
      if (isAdmin) loadStatistics();
    },
    onTicketDeleted: (data) => {
      console.log('Ticket deleted:', data);
      const ticketId = data.ticket_id || data.id;
      // Remove ticket from list
      setTickets(prevTickets => prevTickets.filter(t => t.id !== ticketId));
      toast.info(`Ticket No: ${ticketId} was deleted`);
      if (isAdmin) loadStatistics();
    },
    onConnectionChange: (status) => {
      console.log('WebSocket connection status:', status);
      if (status === 'connected') {
        console.log('Real-time updates enabled');
      } else if (status === 'failed') {
        console.warn('Real-time updates unavailable');
      }
    },
  });

  // Check if user is admin
  useEffect(() => {
    fetch(`${BASE_URL}/api/user-profile/`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('jwt_token')}`,
      },
    })
      .then((res) => res.json())
      .then((data) => {
        setIsAdmin(data.is_admin);
        if (data.is_admin) {
          loadStatistics();
        }
      })
      .catch((err) => {
        console.error('Error fetching user data:', err);
      });
  }, []);

  // Fetch tickets on mount and when filters change
  useEffect(() => {
    fetchTickets();
  }, [filters]);

  // Apply search filter
  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredTickets(tickets);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = tickets.filter(
        (ticket) =>
          ticket.title.toLowerCase().includes(query) ||
          ticket.description.toLowerCase().includes(query) ||
          ticket.id.toString().includes(query)
      );
      setFilteredTickets(filtered);
    }
  }, [searchQuery, tickets]);

  const fetchTickets = async () => {
    try {
      setIsLoading(true);
      const data = await getTickets(filters);
      setTickets(data);
      setFilteredTickets(data);
    } catch (error) {
      console.error('Error fetching tickets:', error);
      if (error.response && error.response.status === 401) {
        toast.error('Session expired. Please login again.');
        navigate('/signin');
      } else {
        toast.error('Failed to load tickets');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const loadStatistics = async () => {
    try {
      const stats = await getStatistics();
      setStatistics(stats);
      setShowStats(true);
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  };

  const handleCreateNew = () => {
    navigate('/user-dashboard/tickets/create');
  };

  const handleDelete = async (id) => {
    const ticket = tickets.find((t) => t.id === id);

    if (!isAdmin && ticket.status !== 'OPEN') {
      toast.error('You can only delete tickets with OPEN status');
      return;
    }

    if (window.confirm('Are you sure you want to delete this ticket?')) {
      try {
        await deleteTicket(id);
        toast.success('Ticket deleted successfully');
        fetchTickets();
        if (isAdmin) {
          loadStatistics();
        }
      } catch (error) {
        console.error('Error deleting ticket:', error);
        if (error.response && error.response.data && error.response.data.error) {
          toast.error(error.response.data.error);
        } else {
          toast.error('Failed to delete ticket');
        }
      }
    }
  };

  const handleFilterChange = (filterType, value) => {
    setFilters({ ...filters, [filterType]: value });
  };

  return (
    <div className="tickets-container">
      {/* Header */}
      <div className="tickets-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <h1 className="tickets-title">Support Tickets</h1>
          {/* WebSocket Status Indicator */}
          {/* <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.5rem 0.75rem',
              borderRadius: '20px',
              fontSize: '0.85rem',
              backgroundColor: isConnected ? 'rgba(0, 200, 83, 0.15)' : 'rgba(108, 117, 125, 0.15)',
              color: isConnected ? '#00c853' : '#6c757d',
              border: `1px solid ${isConnected ? 'rgba(0, 200, 83, 0.3)' : 'rgba(108, 117, 125, 0.3)'}`,
            }}
            title={isConnected ? 'Real-time updates enabled' : 'Real-time updates unavailable'}
          >
            {isConnected ? (
              <>
                <WifiIcon sx={{ fontSize: 16 }} />
                <span>Live</span>
              </>
            ) : (
              <>
                <WifiOffIcon sx={{ fontSize: 16 }} />
                <span>Offline</span>
              </>
            )}
          </div> */}
        </div>
        <button className="btn-create-ticket" onClick={handleCreateNew}>
          <AddIcon sx={{ fontSize: 20 }} />
          Create New Ticket
        </button>
      </div>

      {/* Statistics (Admin Only) */}
      {isAdmin && showStats && statistics && (
        <div className="stats-grid">
          <div className="stat-card">
            <h2 className="stat-value">{statistics.total_tickets}</h2>
            <p className="stat-label">Total Tickets</p>
          </div>
          <div className="stat-card">
            <h2 className="stat-value">{statistics.by_status.open}</h2>
            <p className="stat-label">Open</p>
          </div>
          <div className="stat-card">
            <h2 className="stat-value">{statistics.by_status.in_progress}</h2>
            <p className="stat-label">In Progress</p>
          </div>
          <div className="stat-card">
            <h2 className="stat-value">{statistics.by_status.closed}</h2>
            <p className="stat-label">Closed</p>
          </div>
          <div className="stat-card">
            <h2 className="stat-value">{statistics.unassigned}</h2>
            <p className="stat-label">Unassigned</p>
          </div>
          <div className="stat-card">
            <h2 className="stat-value">{statistics.by_priority.critical}</h2>
            <p className="stat-label">Critical Priority</p>
          </div>
        </div>
      )}

      {/* Filters and Search */}
      <div className="tickets-filters">
        <div className="filter-group">
          <label className="filter-label">Status</label>
          <select
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            className="filter-select"
          >
            <option value="all">All Statuses</option>
            <option value="open">Open</option>
            <option value="in_progress">In Progress</option>
            <option value="closed">Closed</option>
          </select>
        </div>

        <div className="filter-group">
          <label className="filter-label">Priority</label>
          <select
            value={filters.priority}
            onChange={(e) => handleFilterChange('priority', e.target.value)}
            className="filter-select"
          >
            <option value="all">All Priorities</option>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="critical">Critical</option>
          </select>
        </div>

        <div className="filter-group">
          <label className="filter-label">Issue Type</label>
          <select
            value={filters.issue_type}
            onChange={(e) => handleFilterChange('issue_type', e.target.value)}
            className="filter-select"
          >
            <option value="all">All Types</option>
            <option value="hardware">Hardware</option>
            <option value="software">Software</option>
            <option value="general_question">Question</option>
            <option value="feature_request">Feature</option>
            <option value="other">Other</option>
          </select>
        </div>

        {isAdmin && (
          <div className="filter-group">
            <label className="filter-label">Assignment</label>
            <select
              value={filters.assigned_to_me ? 'me' : 'all'}
              onChange={(e) =>
                handleFilterChange('assigned_to_me', e.target.value === 'me')
              }
              className="filter-select"
            >
              <option value="all">All Tickets</option>
              <option value="me">Assigned to Me</option>
            </select>
          </div>
        )}

        <div className="filter-group" style={{ flex: 1 }}>
          <label className="filter-label">Search</label>
          <input
            type="text"
            placeholder="Search tickets by title, description, or ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p className="loading-text">Loading tickets...</p>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && filteredTickets.length === 0 && (
        <div className="empty-state">
          <div className="empty-state-icon">📋</div>
          <h2 className="empty-state-title">
            {searchQuery
              ? 'No tickets found'
              : 'No tickets yet'}
          </h2>
          <p className="empty-state-text">
            {searchQuery
              ? 'Try adjusting your search or filters'
              : 'Create your first support ticket to get started'}
          </p>
          {!searchQuery && (
            <button className="btn-create-ticket" onClick={handleCreateNew}>
              <AddIcon sx={{ fontSize: 20 }} />
              Create First Ticket
            </button>
          )}
        </div>
      )}

      {/* Tickets Grid */}
      {!isLoading && filteredTickets.length > 0 && (
        <div className="tickets-grid">
          {filteredTickets.map((ticket) => (
            <TicketCard
              key={ticket.id}
              ticket={ticket}
              isAdmin={isAdmin}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default TicketList;
